import type { EntityType, FieldType } from "@/types/fieldRegistry";
import { getComparisonOperatorsForField } from "@/types/fieldRegistry";

export type SelectOption = string | { value: string; label: string };

export interface FieldOption {
  value: string;
  label: string;
  type: FieldType;
  options?: SelectOption[];
  /** Field needs options loaded at runtime (e.g. pipeline stages) */
  dynamicOptions?: 'stages' | 'lead_sources' | 'pipelines' | 'loss_reasons' | 'sequences_lead' | 'sequences_deal';
}

/** Campos virtuais de cadência. Resolvidos contra sequence_enrollments/deal_sequence_enrollments via RPC. */
export const CADENCE_FIELD_SEQUENCE_LEAD = "leads.cadence_sequence_id";
export const CADENCE_FIELD_STATUS_LEAD = "leads.cadence_status";
export const CADENCE_FIELD_STATE_LEAD = "leads.cadence_state";
export const CADENCE_FIELD_SEQUENCE_DEAL = "deals.cadence_sequence_id";
export const CADENCE_FIELD_STATUS_DEAL = "deals.cadence_status";
export const CADENCE_FIELD_STATE_DEAL = "deals.cadence_state";

const CADENCE_FIELD_PATHS = new Set<string>([
  CADENCE_FIELD_SEQUENCE_LEAD,
  CADENCE_FIELD_STATUS_LEAD,
  CADENCE_FIELD_STATE_LEAD,
  CADENCE_FIELD_SEQUENCE_DEAL,
  CADENCE_FIELD_STATUS_DEAL,
  CADENCE_FIELD_STATE_DEAL,
]);

export function isCadenceFilterField(fieldValue: string): boolean {
  return CADENCE_FIELD_PATHS.has(fieldValue);
}

const CADENCE_STATUS_OPTIONS: SelectOption[] = [
  { value: "active", label: "Ativa" },
  { value: "paused", label: "Pausada" },
  { value: "completed", label: "Concluída" },
  { value: "cancelled", label: "Cancelada" },
];

const CADENCE_STATE_OPTIONS: SelectOption[] = [
  { value: "never_enrolled", label: "Nunca inscrito" },
  { value: "no_active", label: "Sem inscrição ativa" },
];

export interface RelationalFieldGroup {
  entity: EntityType;
  label: string;
  relationPath: string;
  relationType: "direct" | "belongsTo" | "hasMany";
  fields: FieldOption[];
}

const DEAL_DIRECT_FIELDS: FieldOption[] = [
  { value: "deals.title", label: "Título", type: "text" },
  { value: "deals.amount", label: "Valor", type: "monetary" },
  { value: "deals.probability", label: "Probabilidade (%)", type: "number" },
  { value: "deals.close_date", label: "Data de Fechamento", type: "date" },
  { value: "deals.won_at", label: "Data de Ganho", type: "date" },
  { value: "deals.lost_at", label: "Data de Perda", type: "date" },
  { value: "deals.pipeline_id", label: "Pipeline", type: "select", dynamicOptions: "pipelines" },
  { value: "deals.stage_id", label: "Etapa", type: "select", dynamicOptions: "stages" },
  { value: "deals.status", label: "Status", type: "select", options: [
    { value: "open", label: "Aberto" },
    { value: "won", label: "Ganho" },
    { value: "lost", label: "Perdido" },
  ]},
  { value: "deals.loss_reason_id", label: "Motivo de Perda", type: "select", dynamicOptions: "loss_reasons" },
  { value: "deals.tags", label: "Tags", type: "labels" },
  { value: "deals.owner_id", label: "Proprietário", type: "user" },
  { value: "deals.sdr_responsavel", label: "SDR Responsável", type: "user" },
  { value: "deals.created_at", label: "Data de Criação", type: "date" },
  { value: "deals.updated_at", label: "Data de Atualização", type: "date" },
  { value: "deals.source", label: "Fonte", type: "select", dynamicOptions: "lead_sources" },
  { value: CADENCE_FIELD_SEQUENCE_DEAL, label: "Cadência", type: "select", dynamicOptions: "sequences_deal" },
  { value: CADENCE_FIELD_STATUS_DEAL, label: "Status da cadência", type: "select", options: CADENCE_STATUS_OPTIONS },
  { value: CADENCE_FIELD_STATE_DEAL, label: "Inscrição em cadência", type: "select", options: CADENCE_STATE_OPTIONS },
];

const LEAD_DIRECT_FIELDS: FieldOption[] = [
  { value: "leads.name", label: "Nome", type: "text" },
  { value: "leads.email", label: "E-mail", type: "email_field" },
  { value: "leads.phone", label: "Telefone", type: "phone" },
  { value: "leads.status", label: "Status", type: "select", options: [
    { value: "new", label: "Novo" },
    { value: "contacted", label: "Contactado" },
    { value: "qualified", label: "Qualificado" },
    { value: "meeting", label: "Reunião" },
    { value: "proposal", label: "Proposta" },
    { value: "won", label: "Ganho" },
    { value: "lost", label: "Perdido" },
  ]},
  { value: "leads.source", label: "Fonte", type: "select", dynamicOptions: "lead_sources" },
  { value: "leads.owner_id", label: "Proprietário", type: "user" },
  { value: "leads.created_at", label: "Data de Criação", type: "date" },
  { value: "leads.updated_at", label: "Data de Atualização", type: "date" },
  { value: "leads.last_contact_at", label: "Último Contato", type: "date" },
  { value: "leads.value", label: "Valor", type: "monetary" },
  { value: CADENCE_FIELD_SEQUENCE_LEAD, label: "Cadência do lead", type: "select", dynamicOptions: "sequences_lead" },
  { value: CADENCE_FIELD_STATUS_LEAD, label: "Status da cadência do lead", type: "select", options: CADENCE_STATUS_OPTIONS },
  { value: CADENCE_FIELD_STATE_LEAD, label: "Inscrição em cadência do lead", type: "select", options: CADENCE_STATE_OPTIONS },
];

const COMPANY_DIRECT_FIELDS: FieldOption[] = [
  { value: "companies.name", label: "Nome", type: "text" },
  { value: "companies.domain", label: "Domínio", type: "text" },
  { value: "companies.website", label: "Website", type: "text" },
  { value: "companies.industry", label: "Segmento", type: "text" },
  { value: "companies.size", label: "Porte", type: "select", options: [
    { value: "micro", label: "Micro" },
    { value: "small", label: "Pequena" },
    { value: "medium", label: "Média" },
    { value: "large", label: "Grande" },
    { value: "enterprise", label: "Enterprise" },
  ]},
  { value: "companies.status", label: "Status", type: "select", options: [
    { value: "prospect", label: "Prospecto" },
    { value: "active", label: "Ativo" },
    { value: "customer", label: "Cliente" },
    { value: "churned", label: "Churned" },
  ]},
  { value: "companies.email", label: "E-mail", type: "email_field" },
  { value: "companies.phone", label: "Telefone", type: "phone" },
  { value: "companies.instagram", label: "Instagram", type: "text" },
  { value: "companies.location", label: "Localização", type: "text" },
  { value: "companies.description", label: "Descrição", type: "textarea" },
  { value: "companies.value", label: "Valor", type: "monetary" },
  { value: "companies.faturamento", label: "Faturamento", type: "text" },
  { value: "companies.total_leads", label: "Total de Leads", type: "number" },
  { value: "companies.active_leads", label: "Leads Ativos", type: "number" },
  { value: "companies.converted_leads", label: "Leads Convertidos", type: "number" },
  { value: "companies.owner_id", label: "Proprietário", type: "user" },
  { value: "companies.pipedrive_id", label: "ID Pipedrive", type: "text" },
  { value: "companies.created_at", label: "Data de Criação", type: "date" },
  { value: "companies.updated_at", label: "Data de Atualização", type: "date" },
  { value: "companies.last_contact_at", label: "Último Contato", type: "date" },
];

const ACTIVITY_DIRECT_FIELDS: FieldOption[] = [
  { value: "activities.title", label: "Título", type: "text" },
  { value: "activities.type", label: "Tipo", type: "select", options: [
    { value: "email", label: "E-mail" },
    { value: "call", label: "Ligação" },
    { value: "meeting", label: "Reunião" },
    { value: "task", label: "Tarefa" },
    { value: "note", label: "Nota" },
    { value: "proposal", label: "Proposta" },
    { value: "whatsapp", label: "WhatsApp" },
  ]},
  { value: "activities.status", label: "Status", type: "select", options: [
    { value: "pending", label: "Pendente" },
    { value: "done", label: "Concluída" },
    { value: "canceled", label: "Cancelada" },
    { value: "paused", label: "Pausada" },
  ]},
  { value: "activities.outcome", label: "Resultado", type: "select", options: [
    { value: "won", label: "Ganho" },
    { value: "lost", label: "Perdido" },
    { value: "pending", label: "Pendente" },
  ]},
  { value: "activities.date", label: "Data", type: "date" },
  { value: "activities.time", label: "Horário", type: "time" },
  { value: "activities.owner_id", label: "Proprietário", type: "user" },
  { value: "activities.created_at", label: "Data de Criação", type: "date" },
  { value: "activities.completed_at", label: "Data de Conclusão", type: "date" },
];

/**
 * Campos diretos de projeto (`work_items` type='project') para o construtor de filtros.
 *
 * Só colunas REAIS. Os agregados que a lista mostra (progresso, total de tarefas, atrasadas)
 * são calculados no `left join lateral` de `get_work_projects_page` e não existem como coluna
 * — oferecê-los aqui geraria push-down de SQL inválido.
 */
export const WORK_ITEM_DIRECT_FIELDS: FieldOption[] = [
  { value: "work_items.title", label: "Nome da entrega", type: "text" },
  { value: "work_items.state", label: "Status", type: "select" },
  { value: "work_items.blocked_by", label: "Bloqueio", type: "select" },
  { value: "work_items.priority", label: "Prioridade", type: "number" },
  { value: "work_items.owner_id", label: "Responsável", type: "user" },
  { value: "work_items.departamento_id", label: "Departamento", type: "select" },
  { value: "work_items.company_id", label: "Empresa", type: "select" },
  { value: "work_items.due_date", label: "Prazo", type: "date" },
  { value: "work_items.start_date", label: "Início", type: "date" },
  { value: "work_items.completed_at", label: "Concluído em", type: "date" },
  { value: "work_items.created_at", label: "Criado em", type: "date" },
];

/**
 * Campos do contrato oferecidos no construtor de filtro.
 *
 * ⚠️ A lista e CURADA, nao o `select *` da tabela. `client_contracts` tem 62 colunas, e a
 * maioria (sync_hash, seeded_from, os 12 campos de cadastro da Receita) nao e criterio de
 * recorte -- oferecer tudo transforma o seletor num despejo em que ninguem acha `MRR`.
 *
 * `monthly_value` e `total_value` sao DERIVADOS por trigger; entram porque filtrar por eles e
 * legitimo, e a edicao continua sendo pelas parcelas.
 */
export const CLIENT_CONTRACT_DIRECT_FIELDS: FieldOption[] = [
  { value: "client_contracts.title", label: "Título", type: "text" },
  { value: "client_contracts.status", label: "Situação", type: "select",
    options: [
      { value: "active", label: "Ativo" },
      { value: "paused", label: "Pausado" },
      { value: "canceling", label: "Em cancelamento" },
      { value: "canceled", label: "Encerrado" },
    ] },
  { value: "client_contracts.stage", label: "Etapa", type: "select",
    options: [
      { value: "kickoff", label: "Kickoff" },
      { value: "setup_crm", label: "Setup CRM" },
      { value: "playbook", label: "Playbook" },
      { value: "go_live", label: "Go-live pleno" },
    ] },
  { value: "client_contracts.payment_mode", label: "Cobrança", type: "select",
    options: [
      { value: "a_vista", label: "À vista" },
      { value: "parcelado", label: "Parcelado" },
      { value: "recorrente_prazo", label: "Recorrente" },
      { value: "recorrente_indeterminado", label: "Recorrente sem prazo" },
      { value: "legado", label: "Legado" },
    ] },
  { value: "client_contracts.monthly_value", label: "MRR", type: "monetary" },
  { value: "client_contracts.total_value", label: "Valor total", type: "monetary" },
  { value: "client_contracts.setup_value", label: "Setup", type: "monetary" },
  { value: "client_contracts.health", label: "Saúde (1–10)", type: "number" },
  { value: "client_contracts.tier", label: "Tier", type: "number" },
  { value: "client_contracts.term_months", label: "Prazo (meses)", type: "number" },
  { value: "client_contracts.installments_count", label: "Nº de parcelas", type: "number" },
  { value: "client_contracts.start_date", label: "Início da vigência", type: "date" },
  { value: "client_contracts.end_date", label: "Fim da vigência", type: "date" },
  { value: "client_contracts.sold_by", label: "Vendido por", type: "user" },
  { value: "client_contracts.company_id", label: "Empresa", type: "select" },
  { value: "client_contracts.sector", label: "Setor", type: "text" },
  { value: "client_contracts.cnpj", label: "CNPJ", type: "text" },
  { value: "client_contracts.legal_name", label: "Razão social", type: "text" },
  { value: "client_contracts.trade_name", label: "Nome fantasia", type: "text" },
  { value: "client_contracts.payment_method", label: "Forma de pagamento", type: "text" },
  { value: "client_contracts.created_at", label: "Criado em", type: "date" },
];

const ENTITY_LABELS: Record<EntityType, string> = {
  deals: "Negócio",
  leads: "Lead",
  companies: "Empresa",
  activities: "Atividade",
  work_items: "Entrega",
  client_contracts: "Contrato",
};

/**
 * Rótulo da entidade relacionada, do ponto de vista da entidade primária.
 *
 * ⚠️ O tipo do NÍVEL DE FORA era `Record<string, …>`, e isso custou um crash em produção
 * (30/08): ao registrar `work_items`, o compilador exigiu a chave nova nos quatro objetos
 * INTERNOS — porque esses são `Record<EntityType, …>` — mas não exigiu o bloco de topo. O
 * resultado foi `RELATED_ENTITY_LABELS['work_items'] === undefined`, e o primeiro
 * `labels[rel.entity]` estourava com "Cannot read properties of undefined".
 *
 * Agora os DOIS níveis são `Record<EntityType, …>`: entidade nova sem bloco aqui vira erro
 * de compilação, não tela branca.
 */
const RELATED_ENTITY_LABELS: Record<EntityType, Record<EntityType, string>> = {
  deals: {
    deals: "Negócio",
    leads: "Lead vinculado",
    companies: "Empresa vinculada",
    activities: "Atividades",
    work_items: "Entregas",
    client_contracts: "Contratos",
  },
  leads: {
    leads: "Lead",
    companies: "Empresa vinculada",
    deals: "Negócios vinculados",
    activities: "Atividades",
    work_items: "Entregas",
    client_contracts: "Contratos",
  },
  companies: {
    companies: "Empresa",
    leads: "Leads vinculados",
    deals: "Negócios vinculados",
    activities: "Atividades",
    work_items: "Entregas",
    client_contracts: "Contratos",
  },
  work_items: {
    work_items: "Entrega",
    companies: "Empresa vinculada",
    deals: "Negócio vinculado",
    leads: "Lead",
    activities: "Atividades",
    client_contracts: "Contratos",
  },
  client_contracts: {
    client_contracts: "Contrato",
    companies: "Empresa cliente",
    deals: "Negócio de origem",
    leads: "Lead",
    activities: "Atividades",
    work_items: "Entregas",
  },
  activities: {
    activities: "Atividade",
    deals: "Negócio vinculado",
    leads: "Lead vinculado",
    companies: "Empresa vinculada",
    work_items: "Entregas",
    client_contracts: "Contratos",
  },
};

type RelationType = "direct" | "belongsTo" | "hasMany";

interface RelationConfig {
  entity: EntityType;
  relationType: RelationType;
  relationPath: string;
}

const ENTITY_RELATIONS: Record<EntityType, RelationConfig[]> = {
  deals: [
    { entity: "deals", relationType: "direct", relationPath: "direct" },
    { entity: "leads", relationType: "belongsTo", relationPath: "leads" },
    { entity: "companies", relationType: "belongsTo", relationPath: "companies" },
    { entity: "activities", relationType: "hasMany", relationPath: "activities" },
  ],
  leads: [
    { entity: "leads", relationType: "direct", relationPath: "direct" },
    { entity: "companies", relationType: "belongsTo", relationPath: "companies" },
    { entity: "deals", relationType: "hasMany", relationPath: "deals" },
    { entity: "activities", relationType: "hasMany", relationPath: "activities" },
  ],
  companies: [
    { entity: "companies", relationType: "direct", relationPath: "direct" },
    { entity: "leads", relationType: "hasMany", relationPath: "leads" },
    { entity: "deals", relationType: "hasMany", relationPath: "deals" },
    { entity: "activities", relationType: "hasMany", relationPath: "activities" },
  ],
  activities: [
    { entity: "activities", relationType: "direct", relationPath: "direct" },
    { entity: "deals", relationType: "belongsTo", relationPath: "deals" },
    { entity: "leads", relationType: "belongsTo", relationPath: "leads" },
    { entity: "companies", relationType: "belongsTo", relationPath: "companies" },
  ],
  // Só empresa e negócio: são os dois vínculos que `work_items` carrega como coluna, e os
  // dois pares que `_filter_field_expr` sabe traduzir. Oferecer `leads` aqui produziria
  // regra que o banco descarta EM SILÊNCIO (a função devolve NULL e o filtro devolve mais
  // linhas do que deveria) — ver 20260830234500_work_items_field_registry.sql.
  work_items: [
    { entity: "work_items", relationType: "direct", relationPath: "direct" },
    { entity: "companies", relationType: "belongsTo", relationPath: "companies" },
    { entity: "deals", relationType: "belongsTo", relationPath: "deals" },
  ],
  // Mesma regra do `work_items` acima, e pelo mesmo motivo: só entram os pares que
  // `_filter_field_expr` sabe traduzir — ensinados em
  // 20260909_filtros_client_contracts_field_expr. `leads` continua de fora porque o contrato
  // não tem `lead_id`: a função devolveria NULL e a regra sumiria do WHERE em silêncio,
  // devolvendo MAIS contratos do que o filtro pede.
  client_contracts: [
    { entity: "client_contracts", relationType: "direct", relationPath: "direct" },
    { entity: "companies", relationType: "belongsTo", relationPath: "companies" },
    { entity: "deals", relationType: "belongsTo", relationPath: "deals" },
  ],
};

const ENTITY_FIELDS: Record<EntityType, FieldOption[]> = {
  deals: DEAL_DIRECT_FIELDS,
  leads: LEAD_DIRECT_FIELDS,
  companies: COMPANY_DIRECT_FIELDS,
  activities: ACTIVITY_DIRECT_FIELDS,
  work_items: WORK_ITEM_DIRECT_FIELDS,
  client_contracts: CLIENT_CONTRACT_DIRECT_FIELDS,
};

function getFieldGroupsForEntity(primaryEntity: EntityType): RelationalFieldGroup[] {
  const relations = ENTITY_RELATIONS[primaryEntity];
  // `?.` mesmo com o tipo estreitado: um `primaryEntity` vindo de dado (filtro salvo antigo,
  // querystring) pode não ser um EntityType de verdade em runtime.
  const labels = RELATED_ENTITY_LABELS[primaryEntity];

  return relations.map((rel) => ({
    entity: rel.entity,
    label: labels?.[rel.entity] || ENTITY_LABELS[rel.entity],
    relationPath: rel.relationPath,
    relationType: rel.relationType,
    fields: ENTITY_FIELDS[rel.entity],
  }));
}

export function getAllFieldsForEntity(primaryEntity: EntityType): FieldOption[] {
  const groups = getFieldGroupsForEntity(primaryEntity);
  return groups.flatMap((g) => g.fields);
}

export function getFieldInfo(
  primaryEntity: EntityType,
  fieldValue: string
): { field: FieldOption; group: RelationalFieldGroup } | null {
  const groups = getFieldGroupsForEntity(primaryEntity);
  for (const group of groups) {
    const field = group.fields.find((f) => f.value === fieldValue);
    if (field) return { field, group };
  }
  return null;
}

/**
 * Regras antigas ou exportadas podem usar prefixo singular (`lead.name`, `deal.title`).
 * O motor e ENTITY_RELATIONS usam sempre o plural canônico (`leads`, `deals`, …).
 */
const CANONICAL_ENTITY_PREFIX: Partial<Record<string, EntityType>> = {
  lead: "leads",
  leads: "leads",
  deal: "deals",
  deals: "deals",
  company: "companies",
  companies: "companies",
  activity: "activities",
  activities: "activities",
  work_item: "work_items",
  work_items: "work_items",
  client_contract: "client_contracts",
  client_contracts: "client_contracts",
};

/** Normaliza o primeiro segmento (`lead.` → `leads.`). Preserva `custom_data.*` e desconhecidos. */
export function normalizeFilterFieldPath(campo: string): string {
  if (!campo || !campo.includes(".")) return campo;
  const dot = campo.indexOf(".");
  const prefix = campo.slice(0, dot);
  const rest = campo.slice(dot + 1);
  const canonical = CANONICAL_ENTITY_PREFIX[prefix];
  if (!canonical) return campo;
  return `${canonical}.${rest}`;
}

export function getRelationTypeForField(
  primaryEntity: EntityType,
  fieldValue: string
): RelationType {
  const normalized = normalizeFilterFieldPath(fieldValue);
  const [entity] = normalized.split(".");
  const relations = ENTITY_RELATIONS[primaryEntity];
  const rel = relations.find((r) => r.entity === entity);
  return rel?.relationType ?? "direct";
}

export function getAvailableEntities(primaryEntity: EntityType): { value: EntityType; label: string }[] {
  const relations = ENTITY_RELATIONS[primaryEntity];
  const labels = RELATED_ENTITY_LABELS[primaryEntity];

  return relations.map((rel) => ({
    value: rel.entity,
    label: labels?.[rel.entity] || ENTITY_LABELS[rel.entity],
  }));
}

export function getFieldsForRelatedEntity(
  primaryEntity: EntityType,
  relatedEntity: EntityType
): FieldOption[] {
  return ENTITY_FIELDS[relatedEntity] || [];
}

/** Operadores do seletor central do filtro (sem relativo / vazio — isso fica na coluna de valor). */
export function getOperatorsForField(fieldType: FieldType) {
  return getComparisonOperatorsForField(fieldType);
}

// ---------------------------------------------------------------------------
// Colunas relacionais (para uso no seletor de colunas das listagens)
//
// Permite que páginas como /deals exibam, na tabela, dados de entidades
// vinculadas (Lead, Empresa) reaproveitando o mesmo registry de campos do
// filtro avançado. Ativa apenas relações belongsTo — relações hasMany
// (ex.: activities em deals) já são representadas por agregados próprios.
// ---------------------------------------------------------------------------
export interface RelationalColumn {
  /** Id usado em ColumnDefinition (`rel_<entity>_<fieldName>`). */
  id: string;
  /** Rótulo curto (sem prefixo de grupo) — ex.: "Faturamento". */
  label: string;
  /** Rótulo da entidade relacionada — ex.: "Empresa vinculada". */
  groupLabel: string;
  /** Entidade relacionada (chave do row hidratado, ex.: deal.companies). */
  entity: EntityType;
  /** Nome da coluna na entidade relacionada (ex.: "faturamento"). */
  fieldName: string;
  /** Definição original do registry (tipo, options, etc.) para formatação. */
  field: FieldOption;
}

const REL_ID_PREFIX = "rel_";

function buildRelationalColumnId(entity: EntityType, fieldName: string): string {
  return `${REL_ID_PREFIX}${entity}_${fieldName}`;
}

/**
 * Gera as colunas relacionais (somente belongsTo) para uma entidade primária.
 * Caller pode passar `skipFieldValues` para suprimir paths já cobertos por
 * colunas nativas (ex.: `companies.name` é equivalente à coluna "company"
 * embutida em /deals).
 */
export function getRelationalColumnsFor(
  primaryEntity: EntityType,
  options?: { skipFieldValues?: ReadonlySet<string> }
): RelationalColumn[] {
  const skip = options?.skipFieldValues ?? new Set<string>();
  const groups = getFieldGroupsForEntity(primaryEntity);
  const out: RelationalColumn[] = [];
  for (const group of groups) {
    if (group.relationType !== "belongsTo") continue;
    const groupLabel = group.label;
    for (const field of group.fields) {
      if (skip.has(field.value)) continue;
      const [, fieldName] = field.value.split(".");
      if (!fieldName) continue;
      out.push({
        id: buildRelationalColumnId(group.entity, fieldName),
        label: field.label,
        groupLabel,
        entity: group.entity,
        fieldName,
        field,
      });
    }
  }
  return out;
}

export function isRelationalColumnId(columnId: string): boolean {
  return columnId.startsWith(REL_ID_PREFIX);
}
